/**
 * /billing — Staff Billing page.
 *
 * Four sections:
 *   1. CROA banner (always visible at the top).
 *   2. Processors — list of registered processors with Configure modal.
 *   3. Invoices — filterable table with per-row actions.
 *   4. New Invoice modal — client + line items (each carrying a
 *      completed_work_ref, the CROA gate enforced server-side).
 */
import RecordPaymentModal from "../components/payments/RecordPaymentModal";
import { payments as paymentsApi, type PaymentsOverview } from "../lib/api";
import { Check, Plus, RefreshCcw, RotateCcw, Send, Settings, ShieldAlert, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Modal as ModalCmp } from "../components/Modal";
import { Shell } from "../components/Shell";
import { StatusPill } from "../components/StatusPill";
import {
  billing,
  clients as clientsApi,
  type ClientListItem,
  type Invoice,
  type InvoiceLineItem,
  type InvoiceStatus,
  type ProcessorListItem,
  ApiError,
} from "../lib/api";

const STATUS_FILTERS: Array<{ value: InvoiceStatus | ""; label: string }> = [
  { value: "", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent (unpaid)" },
  { value: "paid", label: "Paid" },
  { value: "void", label: "Void" },
  { value: "refunded", label: "Refunded" },
];

const REF_KIND_OPTIONS: Array<{ value: InvoiceLineItem["completed_work_ref"]["kind"]; label: string }> = [
  { value: "dispute_round", label: "Dispute round" },
  { value: "letter", label: "Letter" },
  { value: "milestone", label: "Manually-attested milestone" },
];

function formatCents(cents: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);
}

function formatDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

export default function BillingPage() {
  const [processors, setProcessors] = useState<ProcessorListItem[]>([]);
  const [futureProcessors, setFutureProcessors] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clientFilter, setClientFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | "">("");
  const [allClients, setAllClients] = useState<ClientListItem[]>([]);
  const [loadingProcessors, setLoadingProcessors] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const [configureProcessor, setConfigureProcessor] = useState<ProcessorListItem | null>(null);

  async function reloadProcessors() {
    setLoadingProcessors(true);
    try {
      const { processors, future_processors } = await billing.listProcessors();
      setProcessors(processors);
      setFutureProcessors(future_processors);
    } catch (e: any) {
      setError(e?.message || "Failed to load processors");
    } finally {
      setLoadingProcessors(false);
    }
  }

  async function reloadInvoices() {
    setLoadingInvoices(true);
    try {
      const list = await billing.listInvoices({
        client_id: clientFilter || undefined,
        status: statusFilter || undefined,
      });
      setInvoices(list);
    } catch (e: any) {
      setError(e?.message || "Failed to load invoices");
    } finally {
      setLoadingInvoices(false);
    }
  }

  useEffect(() => {
    reloadProcessors();
    clientsApi.list({ limit: 500 }).then((r) => setAllClients(r.items)).catch(() => {});
  }, []);

  useEffect(() => {
    reloadInvoices();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [clientFilter, statusFilter]);

  return (
    <Shell
      title="Billing"
      subtitle="Invoices for completed work"
      actions={
        <>
          <button className="btn-ghost" onClick={() => { reloadProcessors(); reloadInvoices(); }}>
            <RefreshCcw size={16} aria-hidden="true" /> Refresh
          </button>
          <button className="btn-primary" onClick={() => setNewInvoiceOpen(true)}>
            <Plus size={16} aria-hidden="true" /> New Invoice
          </button>
        </>
      }
    >
      {/* ── CROA Banner ──────────────────────────────────────────────── */}
      <div className="banner-warning banner-rail" role="note" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <ShieldAlert size={20} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong>CROA compliance — invoice-after-completion only.</strong>
            <p className="small" style={{ marginTop: 4, marginBottom: 0 }}>
              Per the Credit Repair Organizations Act, fees may only be invoiced
              <strong> after services are fully performed</strong>. The system enforces this by
              requiring a <code>completed_work_ref</code> on every line item — a dispute round
              id, a letter id, or a manually-attested milestone. No upfront retainers, no
              recurring subscriptions, no billing-on-signup are supported by this page.
            </p>
          </div>
        </div>
      </div>

      {flash && (
        <div
          className={`${flash.kind === "ok" ? "banner-success" : "banner-error"} banner-rail`}
          role={flash.kind === "ok" ? "status" : "alert"}
          aria-live={flash.kind === "ok" ? "polite" : "assertive"}
          style={{ marginBottom: 12 }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{flash.text}</span>
            <button
              className="icon-button"
              onClick={() => setFlash(null)}
              title="Dismiss"
              aria-label="Dismiss notification"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="banner-error banner-rail" role="alert" style={{ marginBottom: 12 }}>
          <p className="small" style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      {/* ── Processors ──────────────────────────────────────────────── */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-header">
          <h2>Payment processors</h2>
          <p>Credit-repair-friendly processors you can charge clients through.</p>
        </div>
        {loadingProcessors ? (
          <p className="muted small">Loading…</p>
        ) : processors.length === 0 ? (
          <p className="muted small">No processors registered.</p>
        ) : (
          <div
            className="table-list"
            data-testid="processors-list"
            role="table"
            aria-label="Payment processors"
          >
            {processors.map((p) => (
              <div
                key={p.id}
                className="table-row"
                role="row"
                style={{ gridTemplateColumns: "minmax(180px, 1fr) 140px 140px 1fr 160px", alignItems: "center" }}
              >
                <div role="cell">
                  <strong>{p.name}</strong>
                  <div className="muted small" style={{ marginTop: 2 }}>{p.id}</div>
                </div>
                <span role="cell">
                  {p.is_configured ? (
                    <span className="status-pill complete"><Check size={11} aria-hidden="true" /> Configured</span>
                  ) : (
                    <span className="status-pill draft">Not configured</span>
                  )}
                </span>
                <span role="cell">
                  {p.is_active ? (
                    <span className="status-pill complete">Active</span>
                  ) : (
                    <span className="muted small">—</span>
                  )}
                </span>
                <span className="muted small" role="cell" style={{ fontSize: 12 }}>
                  {[
                    p.supports_hosted_checkout ? "hosted checkout" : null,
                    p.supports_direct_charge ? "direct charge" : null,
                  ].filter(Boolean).join(" · ")}
                  <div style={{ marginTop: 2, color: "var(--ne-muted)" }}>{p.description}</div>
                </span>
                <span role="cell" style={{ textAlign: "right" }}>
                  <button
                    className="btn-secondary"
                    style={{ height: 32, padding: "0 12px", fontSize: 12 }}
                    onClick={() => setConfigureProcessor(p)}
                    aria-label={`Configure ${p.name}`}
                  >
                    <Settings size={13} aria-hidden="true" /> Configure
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}

      </div>

      {/* ── Invoices ────────────────────────────────────────────────── */}
      <div className="panel">
        <div className="panel-header">
          <h2>Invoices</h2>
          <p>One row per invoice. Sent invoices link to the active processor's pay flow.</p>
        </div>

        <div className="filters-row" style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "center" }}>
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            aria-label="Filter invoices by client"
            style={{ minWidth: 220 }}
          >
            <option value="">All clients</option>
            {allClients.map((c) => (
              <option key={c.id} value={c.id}>{c.legal_name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            aria-label="Filter invoices by status"
            style={{ minWidth: 180 }}
          >
            {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <span className="muted small" aria-live="polite">{invoices.length} invoice{invoices.length === 1 ? "" : "s"}</span>
        </div>

        {loadingInvoices ? (
          <p className="muted small">Loading invoices…</p>
        ) : invoices.length === 0 ? (
          <p className="muted small">No invoices match the current filters.</p>
        ) : (
          <div
            className="table-list"
            data-testid="invoices-list"
            role="table"
            aria-label="Invoices"
          >
            <div
              className="table-row"
              role="row"
              style={{
                gridTemplateColumns: "100px minmax(160px, 1fr) 110px 100px 130px 110px 1fr",
                background: "var(--ne-silver-3)",
                fontWeight: 600,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 1,
                color: "var(--ne-text-3)",
              }}
            >
              <span role="columnheader">Invoice</span>
              <span role="columnheader">Client</span>
              <span role="columnheader">Amount</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Processor</span>
              <span role="columnheader">Created</span>
              <span role="columnheader" style={{ textAlign: "right" }}>Actions</span>
            </div>
            {invoices.map((inv) => (
              <InvoiceRow
                key={inv.id}
                invoice={inv}
                processors={processors}
                onAction={async (action, extra) => {
                  try {
                    if (action === "send") {
                      const res = await billing.sendInvoice(inv.id, extra);
                      // Show the exact link the client received so staff can
                      // re-send it by text or open it to check the pay page.
                      setFlash({
                        kind: "ok",
                        text: `Invoice sent via ${res.processor} to ${res.recipient_email}${res.test_mode ? " (test mode)" : ""}. Pay link: ${res.pay_url}`,
                      });
                    } else if (action === "void") {
                      await billing.voidInvoice(inv.id);
                      setFlash({ kind: "ok", text: `Invoice ${inv.id.slice(0, 8)} voided.` });
                    } else if (action === "refund") {
                      await billing.refundInvoice(inv.id);
                      setFlash({ kind: "ok", text: `Invoice ${inv.id.slice(0, 8)} refunded.` });
                    } else if (action === "refresh") {
                      setFlash({ kind: "ok", text: `Payment recorded on invoice ${inv.id.slice(0, 8)}.` });
                    }
                    reloadInvoices();
                  } catch (e: any) {
                    const code = (e instanceof ApiError && e.body?.error) || "error";
                    const msg = (e instanceof ApiError && (e.body?.message || e.body?.error)) || e?.message || String(e);
                    setFlash({ kind: "err", text: `${code}: ${msg}` });
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>

      {newInvoiceOpen && (
        <NewInvoiceModal
          allClients={allClients}
          processors={processors}
          onClose={() => setNewInvoiceOpen(false)}
          onCreated={() => {
            setNewInvoiceOpen(false);
            setFlash({ kind: "ok", text: "Draft invoice created." });
            reloadInvoices();
          }}
          onError={(msg) => setFlash({ kind: "err", text: msg })}
        />
      )}

      {configureProcessor && (
        <ConfigureProcessorModal
          processor={configureProcessor}
          onClose={() => setConfigureProcessor(null)}
          onSaved={() => {
            setConfigureProcessor(null);
            setFlash({ kind: "ok", text: "Processor credentials saved." });
            reloadProcessors();
          }}
        />
      )}
    </Shell>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Per-row component (keeps state for send-modal local to each row)
// ────────────────────────────────────────────────────────────────────────
function InvoiceRow({
  invoice,
  processors,
  onAction,
}: {
  invoice: Invoice;
  processors: ProcessorListItem[];
  onAction: (
    action: "send" | "void" | "refund" | "refresh",
    extra?: { processor?: string; test_mode?: boolean },
  ) => void | Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const shortId = invoice.id.slice(0, 8);
  return (
    <>
      <div
        className="table-row"
        role="row"
        style={{ gridTemplateColumns: "100px minmax(160px, 1fr) 110px 100px 130px 110px 1fr", alignItems: "center" }}
      >
        <span role="cell" style={{ fontFamily: "monospace", fontSize: 11 }}>{shortId}</span>
        <span role="cell">
          <strong>{invoice.client_name || "—"}</strong>
          <div className="muted small">{invoice.client_email || ""}</div>
        </span>
        <span role="cell" style={{ fontFamily: "var(--ne-display)", fontSize: 17 }}>
          {formatCents(invoice.total_cents, invoice.currency)}
        </span>
        <span role="cell"><StatusPill status={invoice.status} /></span>
        <span role="cell" className="muted small">{invoice.paid_via_processor || "—"}</span>
        <span role="cell" className="muted small">{formatDate(invoice.created_at)}</span>
        <span role="cell" style={{ textAlign: "right", display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button
            className="btn-ghost"
            style={{ height: 28, padding: "0 10px", fontSize: 12 }}
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={`${expanded ? "Hide" : "View"} invoice ${shortId} details`}
          >
            {expanded ? "Hide" : "View"}
          </button>
          {(invoice.status === "draft" || invoice.status === "sent") && (
            <button
              className="btn-secondary"
              style={{ height: 28, padding: "0 10px", fontSize: 12 }}
              onClick={() => setSendOpen(true)}
              aria-label={`${invoice.status === "sent" ? "Resend" : "Send"} invoice ${shortId}`}
            >
              <Send size={11} aria-hidden="true" /> {invoice.status === "sent" ? "Resend" : "Send"}
            </button>
          )}
          {(invoice.status === "draft" || invoice.status === "sent") && (
            <button
              className="btn-secondary"
              style={{ height: 28, padding: "0 10px", fontSize: 12 }}
              onClick={() => setRecordOpen(true)}
              aria-label={`Mark paid or record a payment on invoice ${shortId}`}
              data-testid="invoice-record-payment"
            >
              Mark paid / Record payment
            </button>
          )}
          {recordOpen && (
            <RecordPaymentForInvoice invoice={invoice} onClose={() => setRecordOpen(false)}
              onSaved={() => { setRecordOpen(false); onAction("refresh"); }} />
          )}
          {invoice.status !== "paid" && invoice.status !== "refunded" && invoice.status !== "void" && (
            <button
              className="btn-ghost"
              style={{ height: 28, padding: "0 10px", fontSize: 12, color: "var(--ne-red-dark)" }}
              onClick={() => { if (confirm(`Void invoice ${shortId}? This cannot be undone.`)) onAction("void"); }}
              aria-label={`Void invoice ${shortId}`}
            >
              <Trash2 size={11} aria-hidden="true" /> Void
            </button>
          )}
          {invoice.status === "paid" && (
            <button
              className="btn-ghost"
              style={{ height: 28, padding: "0 10px", fontSize: 12 }}
              onClick={() => { if (confirm(`Refund invoice ${shortId}?`)) onAction("refund"); }}
              aria-label={`Refund invoice ${shortId}`}
            >
              <RotateCcw size={11} aria-hidden="true" /> Refund
            </button>
          )}
        </span>
      </div>
      {expanded && (
        <div className="table-row" role="row" style={{ gridTemplateColumns: "1fr", background: "var(--ne-surface-subtle)", padding: 16 }}>
          <div role="cell">
            {invoice.description && (
              <p style={{ marginTop: 0, fontSize: 14 }}>{invoice.description}</p>
            )}
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--ne-silver-1)", textAlign: "left" }}>
                  <th style={{ padding: "6px 4px", fontWeight: 600 }}>Description</th>
                  <th style={{ padding: "6px 4px", fontWeight: 600 }}>Completed work</th>
                  <th style={{ padding: "6px 4px", fontWeight: 600, textAlign: "right" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.line_items_json || []).map((li, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--ne-silver-4)" }}>
                    <td style={{ padding: "6px 4px" }}>{li.description}</td>
                    <td style={{ padding: "6px 4px", color: "var(--ne-text-3)" }}>
                      <span className="status-pill" style={{ fontSize: 10, marginRight: 6 }}>
                        {li.completed_work_ref?.kind || "—"}
                      </span>
                      {li.completed_work_ref?.label || ""}
                    </td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "monospace" }}>
                      {formatCents(li.amount_cents, invoice.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--ne-muted)" }}>
              <span>
                Created {formatDate(invoice.created_at)}{invoice.sent_at ? ` · Sent ${formatDate(invoice.sent_at)}` : ""}
                {invoice.paid_at ? ` · Paid ${formatDate(invoice.paid_at)}` : ""}
                {invoice.voided_at ? ` · Voided ${formatDate(invoice.voided_at)}` : ""}
                {invoice.refunded_at ? ` · Refunded ${formatDate(invoice.refunded_at)}` : ""}
              </span>
              {invoice.processor_charge_id && <span>charge id <code>{invoice.processor_charge_id}</code></span>}
            </div>
          </div>
        </div>
      )}
      {sendOpen && (
        <SendInvoiceModal
          invoice={invoice}
          processors={processors}
          onClose={() => setSendOpen(false)}
          onSend={(extra) => { setSendOpen(false); onAction("send", extra); }}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Send invoice modal
// ────────────────────────────────────────────────────────────────────────
function SendInvoiceModal({
  invoice,
  processors,
  onClose,
  onSend,
}: {
  invoice: Invoice;
  processors: ProcessorListItem[];
  onClose: () => void;
  onSend: (extra: { processor?: string; test_mode?: boolean }) => void;
}) {
  const configured = processors.filter((p) => p.is_configured);
  const [processor, setProcessor] = useState<string>(
    invoice.paid_via_processor || configured[0]?.id || ""
  );
  const [testMode, setTestMode] = useState(true);
  const noneConfigured = configured.length === 0;

  return (
    <ModalCmp open onClose={onClose} title={`Send invoice ${invoice.id.slice(0, 8)}`}
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={noneConfigured || !processor}
            onClick={() => onSend({ processor: processor || undefined, test_mode: testMode })}
          >
            <Send size={14} aria-hidden="true" /> {invoice.status === "sent" ? "Resend" : "Send"} invoice
          </button>
        </>
      }
    >
      {noneConfigured && (
        <p className="error-text" style={{ margin: 0 }}>
          No processor is configured. Configure Authorize.net or Fanbasis above before sending.
        </p>
      )}
      <div className="form-field">
        <label>Processor</label>
        <select value={processor} onChange={(e) => setProcessor(e.target.value)}>
          <option value="">Pick a processor…</option>
          {configured.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {p.supports_hosted_checkout ? "hosted checkout" : "direct charge"}
            </option>
          ))}
        </select>
      </div>
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
        <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} />
        Test mode — force the email to Alieffinancial@gmail.com.
      </label>
      <p className="muted small" style={{ margin: 0 }}>
        Amount: <strong>{formatCents(invoice.total_cents, invoice.currency)}</strong>
        {invoice.client_email ? ` · client email ${invoice.client_email}` : ""}
      </p>
    </ModalCmp>
  );
}

// ────────────────────────────────────────────────────────────────────────
// New Invoice modal
// ────────────────────────────────────────────────────────────────────────
function NewInvoiceModal({
  allClients,
  processors,
  onClose,
  onCreated,
  onError,
}: {
  allClients: ClientListItem[];
  processors: ProcessorListItem[];
  onClose: () => void;
  onCreated: () => void;
  onError: (msg: string) => void;
}) {
  const [clientId, setClientId] = useState<string>("");
  const [description, setDescription] = useState("");
  const [dueAt, setDueAt] = useState<string>("");
  const [lines, setLines] = useState<InvoiceLineItem[]>([
    {
      description: "",
      amount_cents: 0,
      completed_work_ref: { kind: "milestone", id: null, label: "" },
    },
  ]);
  const [submitting, setSubmitting] = useState(false);

  const totalCents = useMemo(
    () => lines.reduce((s, l) => s + (Number.isFinite(l.amount_cents) ? l.amount_cents : 0), 0),
    [lines],
  );

  function updateLine(i: number, patch: Partial<InvoiceLineItem>) {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function updateRef(i: number, patch: Partial<InvoiceLineItem["completed_work_ref"]>) {
    setLines((prev) =>
      prev.map((l, idx) => (idx === i ? { ...l, completed_work_ref: { ...l.completed_work_ref, ...patch } } : l)),
    );
  }
  function addLine() {
    setLines((prev) => [
      ...prev,
      { description: "", amount_cents: 0, completed_work_ref: { kind: "milestone", id: null, label: "" } },
    ]);
  }
  function removeLine(i: number) {
    setLines((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function save() {
    if (!clientId) return onError("Pick a client.");
    if (lines.length === 0) return onError("Add at least one line item.");
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.description.trim()) return onError(`Line ${i + 1}: description is required.`);
      if (!Number.isFinite(l.amount_cents) || l.amount_cents <= 0 || !Number.isInteger(l.amount_cents))
        return onError(`Line ${i + 1}: amount must be a positive integer (cents).`);
      if (!l.completed_work_ref.label.trim())
        return onError(`Line ${i + 1}: completed work label is required (CROA).`);
    }
    setSubmitting(true);
    try {
      await billing.createInvoice({
        client_id: clientId,
        line_items: lines,
        description: description || undefined,
        due_at: dueAt ? new Date(dueAt).toISOString() : undefined,
      });
      onCreated();
    } catch (e: any) {
      const code = (e instanceof ApiError && e.body?.error) || "error";
      const msg = (e instanceof ApiError && (e.body?.message || e.body?.error)) || e?.message || String(e);
      onError(`${code}: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalCmp open onClose={onClose} title="New invoice — for completed work" wide
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={submitting}>
            {submitting ? "Saving…" : "Save draft"}
          </button>
        </>
      }
    >
      <div className="form-field">
        <label>Client</label>
        <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Pick a client…</option>
          {allClients.map((c) => (
            <option key={c.id} value={c.id}>{c.legal_name}{c.email ? ` — ${c.email}` : ""}</option>
          ))}
        </select>
      </div>
      <div className="form-field">
        <label>Description (optional)</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this invoice is for" />
      </div>
      <div className="form-field">
        <label>Due date (optional)</label>
        <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
      </div>

      <div>
        <strong style={{ fontSize: 13 }}>Line items</strong>
        <p className="muted small" style={{ marginTop: 2 }}>
          Every line item must reference completed work. This is enforced server-side per CROA.
        </p>
        {lines.map((line, i) => (
          <div
            key={i}
            style={{
              border: "1px solid var(--ne-silver-1)",
              padding: 12,
              marginTop: 8,
              background: "var(--ne-surface-subtle)",
              display: "grid",
              gap: 10,
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 40px", gap: 8, alignItems: "end" }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label>Description</label>
                <input
                  value={line.description}
                  onChange={(e) => updateLine(i, { description: e.target.value })}
                  placeholder="e.g. Round 1 dispute letters delivered (3 bureaus)"
                />
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label>Amount (cents)</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={Number.isFinite(line.amount_cents) ? line.amount_cents : 0}
                  onChange={(e) => updateLine(i, { amount_cents: parseInt(e.target.value || "0", 10) })}
                />
              </div>
              <button
                className="icon-button"
                onClick={() => removeLine(i)}
                title="Remove line"
                aria-label={`Remove line item ${i + 1}`}
                style={{ height: 36 }}
                disabled={lines.length === 1}
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8 }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label>Completed work kind</label>
                <select
                  value={line.completed_work_ref.kind}
                  onChange={(e) => updateRef(i, { kind: e.target.value as any })}
                >
                  {REF_KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label>Completed work label (required — what was done)</label>
                <input
                  value={line.completed_work_ref.label}
                  onChange={(e) => updateRef(i, { label: e.target.value })}
                  placeholder="e.g. Round 1 sent 2026-06-15 / Letter R2-EQ delivered / Manual milestone: review complete"
                />
              </div>
            </div>
          </div>
        ))}
        <button className="btn-ghost" onClick={addLine} style={{ marginTop: 8 }}>
          <Plus size={14} aria-hidden="true" /> Add line item
        </button>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--ne-silver-1)" }}>
        <span className="muted small">{lines.length} line item{lines.length === 1 ? "" : "s"}</span>
        <strong style={{ fontFamily: "var(--ne-display)", fontSize: 22 }}>
          {formatCents(totalCents)}
        </strong>
      </div>
    </ModalCmp>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Configure processor modal
// ────────────────────────────────────────────────────────────────────────
function ConfigureProcessorModal({
  processor,
  onClose,
  onSaved,
}: {
  processor: ProcessorListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Each processor needs a different payload shape — pick the right form.
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState<string>("");
  const [payload, setPayload] = useState<Record<string, string>>({});

  function setField(key: string, value: string) {
    setPayload((p) => ({ ...p, [key]: value }));
  }

  const fields = useMemo<{ key: string; label: string; placeholder?: string; type?: string; required?: boolean }[]>(() => {
    if (processor.id === "authorize_net") {
      return [
        { key: "api_login_id", label: "API Login ID", required: true },
        { key: "transaction_key", label: "Transaction Key", required: true, type: "password" },
        // Without this the client's pay page has no card form — the Public
        // Client Key is what Accept.js uses to encrypt the card in the browser.
        { key: "client_key", label: "Public Client Key (Authorize.net → Account → Security Settings → Manage Public Client Key) — required for clients to pay online", required: true },
        { key: "signature_key", label: "Signature Key (webhook HMAC)", type: "password" },
        { key: "mode", label: "Mode — production (live) or sandbox (test). Leave blank for production.", placeholder: "production" },
      ];
    }
    if (processor.id === "fanbasis") {
      return [
        { key: "api_key", label: "API Key", required: true, type: "password" },
        { key: "webhook_secret", label: "Webhook Secret", type: "password" },
        { key: "api_base", label: "API Base (defaults to https://api.fanbasis.com)" },
      ];
    }
    return [{ key: "raw", label: "JSON credentials", required: true }];
  }, [processor.id]);

  async function save() {
    setError(null);
    const required = fields.filter((f) => f.required).map((f) => f.key);
    for (const k of required) {
      if (!payload[k]?.trim()) return setError(`${k} is required.`);
    }
    setSubmitting(true);
    try {
      const cleaned: Record<string, unknown> = {};
      for (const f of fields) {
        const v = payload[f.key];
        if (v != null && v !== "") cleaned[f.key] = v;
      }
      // A merchant entering their own keys is live unless they say sandbox.
      if (processor.id === "authorize_net") {
        const m = String(cleaned.mode || "").trim().toLowerCase();
        if (m !== "sandbox" && m !== "production") cleaned.mode = "production";
        else cleaned.mode = m;
      }
      await billing.saveProcessorCredentials(processor.id, cleaned, {
        label: label || undefined,
        activate: true,
      });
      onSaved();
    } catch (e: any) {
      const code = (e instanceof ApiError && e.body?.error) || "error";
      const msg = (e instanceof ApiError && (e.body?.message || e.body?.error)) || e?.message || String(e);
      setError(`${code}: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalCmp open onClose={onClose} title={`Configure ${processor.name}`}
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={submitting}>
            {submitting ? "Saving…" : "Save & activate"}
          </button>
        </>
      }
    >
      <p className="muted small" style={{ marginTop: 0 }}>{processor.description}</p>
      <p className="muted small" style={{ marginTop: 0 }}>
        Credentials are encrypted at rest via pgcrypto (<code>PII_ENCRYPTION_KEY</code>) and never returned in plaintext via the API.
      </p>
      {error && (
        <p className="error-text" role="alert" style={{ margin: 0 }}>{error}</p>
      )}
      <div className="form-field">
        <label>Label (optional — e.g. "Live account" / "Sandbox")</label>
        <input value={label} onChange={(e) => setLabel(e.target.value)} />
      </div>
      {fields.map((f) => (
        <div className="form-field" key={f.key}>
          <label>{f.label}{f.required ? " *" : ""}</label>
          <input
            type={f.type || "text"}
            value={payload[f.key] || ""}
            placeholder={f.placeholder}
            onChange={(e) => setField(f.key, e.target.value)}
            autoComplete="off"
          />
        </div>
      ))}
    </ModalCmp>
  );
}

/** Billing-page shortcut: load the client's open invoices/rounds, then open
 *  Record payment pre-pointed at this invoice. */
function RecordPaymentForInvoice({ invoice, onClose, onSaved }: { invoice: Invoice; onClose: () => void; onSaved: () => void }) {
  const [overview, setOverview] = useState<PaymentsOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    paymentsApi.overview(invoice.client_id)
      .then((d) => { if (!cancelled) setOverview(d); })
      .catch((e) => { if (!cancelled) setErr(e?.body?.message || e?.message || "Couldn't load this client's billing."); });
    return () => { cancelled = true; };
  }, [invoice.client_id]);
  if (err) return <ModalCmp open onClose={onClose} title="Record payment"><p className="error-text" role="alert">{err}</p></ModalCmp>;
  if (!overview) return <ModalCmp open onClose={onClose} title="Record payment"><p className="muted">Loading…</p></ModalCmp>;
  return <RecordPaymentModal clientId={invoice.client_id} overview={overview} presetInvoiceId={invoice.id} onClose={onClose} onSaved={() => onSaved()} />;
}
